import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { PageMeta } from "@/components/PageMeta";
import { Trophy } from "lucide-react";
import { toast } from "sonner";

interface FieldErrors {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}

/** Resolve where to send the user after auth completes. */
const isSafeRedirect = (p: string | null | undefined): p is string =>
  !!p && p.startsWith("/") && !p.startsWith("//");

const Auth = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const redirectTarget = useMemo(() => {
    const qp = searchParams.get("redirect");
    if (isSafeRedirect(qp)) return qp;
    const pendingInvite = typeof window !== "undefined" ? sessionStorage.getItem("pendingInvite") : null;
    if (pendingInvite) return `/invite/${pendingInvite}`;
    return "/";
  }, [searchParams]);

  const defaultTab = searchParams.get("tab") === "signup" ? "signup" : "signin";

  useEffect(() => { if (!loading && user) navigate(redirectTarget, { replace: true }); }, [user, loading, navigate, redirectTarget]);


  const validateSignup = (): FieldErrors => {
    const errors: FieldErrors = {};
    const trimmedName = name.trim();
    const trimmedEmail = email.trim();

    if (!trimmedName || trimmedName.length < 2) {
      errors.name = t("auth.nameMinLength");
    }
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      errors.email = t("auth.invalidEmail");
    }
    if (password.length < 6) {
      errors.password = t("auth.passwordMinLength");
    } else if (!/\d/.test(password) && !/[^a-zA-Z0-9]/.test(password)) {
      errors.password = t("auth.passwordWeak");
    }
    if (password !== confirmPassword) {
      errors.confirmPassword = t("auth.passwordsDontMatch");
    }
    return errors;
  };

  const signIn = async () => {
    setBusy(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setBusy(false);
    if (error) toast.error(error.message); else navigate(redirectTarget, { replace: true });
  };

  const signUp = async () => {
    const errors = validateSignup();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setBusy(true);
    const { error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        emailRedirectTo: `${window.location.origin}${redirectTarget}`,
        data: { name: name.trim() || email.split("@")[0], language: i18n.language },
      },
    });
    setBusy(false);
    if (error) {
      const msg = error.message.toLowerCase();
      if (msg.includes("already registered") || msg.includes("already exists") || msg.includes("user already")) {
        toast.error(t("auth.emailAlreadyExists"));
      } else {
        toast.error(error.message);
      }
    } else { toast.success(t("auth.created")); setConfirmPassword(""); setFieldErrors({}); }
  };

  const signInGoogle = async () => {
    setBusy(true);
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: `${window.location.origin}${redirectTarget}`,
    });
    if (result.error) { setBusy(false); toast.error(result.error.message); return; }
    if (result.redirected) return;
    setBusy(false);
    navigate(redirectTarget, { replace: true });
  };


  return (
    <div className="min-h-screen bg-gradient-hero text-primary-foreground">
      <PageMeta
        title="Přihlášení — Tipomatic"
        description="Přihlas se nebo si vytvoř účet a začni tipovat zápasy s kamarády v soukromé lize."
        path="/auth"
      />
      <div className="absolute top-3 right-3"><LanguageSwitcher /></div>
      <div className="container max-w-md px-4 py-10">
        <Link to="/" className="mb-8 flex items-center justify-center gap-2 text-2xl font-bold">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-accent text-accent-foreground shadow-gold">
            <Trophy className="h-5 w-5" />
          </div>
          {t("auth.title")}
        </Link>
        <h1 className="text-center text-2xl font-bold mb-2">{t("auth.signIn")}</h1>
        <p className="text-center mb-6 opacity-80">{t("auth.tagline")}</p>
        <Card className="text-foreground">
          <CardContent className="pt-6">
            <Tabs defaultValue={defaultTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="signin">{t("auth.signIn")}</TabsTrigger>
                <TabsTrigger value="signup">{t("auth.signUp")}</TabsTrigger>
              </TabsList>
              <TabsContent value="signin" className="space-y-4 pt-4">
                <div className="space-y-2"><Label>{t("auth.email")}</Label><Input type="email" value={email} onChange={e => setEmail(e.target.value)} /></div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>{t("auth.password")}</Label>
                    <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2">
                      {t("auth.forgotPassword")}
                    </Link>
                  </div>
                  <Input type="password" value={password} onChange={e => setPassword(e.target.value)} />
                </div>
                <Button className="w-full h-11 bg-gradient-pitch" disabled={busy} onClick={signIn}>{t("auth.signInBtn")}</Button>
                <div className="flex items-center gap-3 text-xs text-muted-foreground"><div className="h-px flex-1 bg-border" />{t("auth.or")}<div className="h-px flex-1 bg-border" /></div>
                <Button type="button" variant="outline" className="w-full h-11" disabled={busy} onClick={signInGoogle}>
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.5-5.1-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.7 6.5 29.1 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.7 6.5 29.1 4.5 24 4.5 16.3 4.5 9.7 8.8 6.3 14.7z"/><path fill="#4CAF50" d="M24 43.5c5 0 9.6-1.9 13.1-5l-6.1-5c-2 1.4-4.4 2.2-7 2.2-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.6 39.1 16.2 43.5 24 43.5z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.1 5c-.4.4 6.6-4.8 6.6-14.5 0-1.2-.1-2.3-.4-3.5z"/></svg>
                  {t("auth.google")}
                </Button>
              </TabsContent>
              <TabsContent value="signup" className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>{t("auth.name")}</Label>
                  <Input value={name} onChange={e => { setName(e.target.value); setFieldErrors(prev => ({ ...prev, name: undefined })); }} />
                  {fieldErrors.name && <p className="text-xs text-red-500">{fieldErrors.name}</p>}
                </div>
                <div className="space-y-2">
                  <Label>{t("auth.email")}</Label>
                  <Input type="email" value={email} onChange={e => { setEmail(e.target.value); setFieldErrors(prev => ({ ...prev, email: undefined })); }} />
                  {fieldErrors.email && <p className="text-xs text-red-500">{fieldErrors.email}</p>}
                </div>
                <div className="space-y-2">
                  <Label>{t("auth.password")}</Label>
                  <Input type="password" value={password} onChange={e => { setPassword(e.target.value); setFieldErrors(prev => ({ ...prev, password: undefined })); }} />
                  {fieldErrors.password && <p className="text-xs text-red-500">{fieldErrors.password}</p>}
                </div>
                <div className="space-y-2">
                  <Label>{t("auth.confirmPassword")}</Label>
                  <Input type="password" value={confirmPassword} onChange={e => { setConfirmPassword(e.target.value); setFieldErrors(prev => ({ ...prev, confirmPassword: undefined })); }} />
                  {fieldErrors.confirmPassword && <p className="text-xs text-red-500">{fieldErrors.confirmPassword}</p>}
                </div>
                <Button className="w-full h-11 bg-gradient-pitch" disabled={busy} onClick={signUp}>{t("auth.signUpBtn")}</Button>
                <div className="flex items-center gap-3 text-xs text-muted-foreground"><div className="h-px flex-1 bg-border" />{t("auth.or")}<div className="h-px flex-1 bg-border" /></div>
                <Button type="button" variant="outline" className="w-full h-11" disabled={busy} onClick={signInGoogle}>
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35.5 24 35.5c-6.4 0-11.5-5.1-11.5-11.5S17.6 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.7 6.5 29.1 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12.5 24 12.5c2.9 0 5.6 1.1 7.6 2.9l5.7-5.7C33.7 6.5 29.1 4.5 24 4.5 16.3 4.5 9.7 8.8 6.3 14.7z"/><path fill="#4CAF50" d="M24 43.5c5 0 9.6-1.9 13.1-5l-6.1-5c-2 1.4-4.4 2.2-7 2.2-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.6 39.1 16.2 43.5 24 43.5z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.1 5c-.4.4 6.6-4.8 6.6-14.5 0-1.2-.1-2.3-.4-3.5z"/></svg>
                  {t("auth.google")}
                </Button>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Auth;
